import React, { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { FormComponentProps } from 'antd/lib/form';
import { inject, observer } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import { Button, Classes, Dialog, Intent, Card } from '@blueprintjs/core';
import withTranslate, { Translate, WithTranslateProps } from '../common/WithTranslate';
import { ApplicationStore, AuthenticationStore } from '../../store';
import { LocaleStore } from '../../store/LocaleStore';
import { Form } from 'react-final-form';
import { TextInputFormField, PasswordInputFormField } from '../common/form';
import { TLCode } from '../../common/enums';
import { IconNames } from '@blueprintjs/icons';
import TranslatedMessage from '../common/translation/TranslatedMessage';
import { PasswordResetMailConfirmation } from './PasswordResetMailConfirmation';
import { Select } from 'antd';
import { css } from 'emotion';
import { UserGuideLink } from '../common/UserGuideLink';
import { uphocLogo, applLogo, cerpanLogo, apnlLogo } from '../../assets';

const LoginCard = styled(Card)`
  width: 24rem;
`;

const LoginContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  .bp3-button {
    margin-right: 3px;
  }
`;

const DivCenter = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding-bottom: 5px;
  justify-content: center;
  .bp3-form-content > div {
    display: flex;
    flex-direction: row;
    margin-right: 20px;
    .bp3-control {
      margin-top: 0;
      margin-right: 5px;
    }
  }
  .bp3-form-group {
    margin-bottom: 0;
  }
`;

const SpanWarning = styled.span`
  padding-left: 23px;
  padding-right: 23px;
`;

const LoginErrorContainer = styled.div`
  display: flex;
  flex-direction: row;
  color: red;
  background-color: WhiteSmoke;
  border-left: 6px solid red;
  margin-bottom: 10px;
  padding: 5px;
`;

interface LoginProps extends FormComponentProps, WithTranslateProps {
  handleLogin: (data: any) => void;
  handleRequestNewPassword: (data: any) => boolean;
  appVersion: string;
  webApiVersion: string | undefined;
  translate: Translate;
  languageSelectionComponent: JSX.Element;
  transferModuleMode: string;
  appStage: string;
  showWarningErrorLogin: boolean;
}

interface LoginState {
  isChangePasswordOpen: boolean;
  isResetPasswordOpen: boolean;
}

interface LoginPageProps extends WithTranslateProps, FormComponentProps {
  ApplicationStore?: Instance<typeof ApplicationStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  LocaleStore?: Instance<typeof LocaleStore>;
}

interface LoginPageState {
  authenticationFailed: boolean;
  transferModuleMode?: string;
  appStage?: string;
}

const Login = ({
  handleLogin,
  handleRequestNewPassword,
  appVersion,
  webApiVersion,
  translate,
  languageSelectionComponent,
  transferModuleMode,
  appStage,
  showWarningErrorLogin,
}: LoginProps) => {
  const [requestNewPasswordInProgress, setRequestNewPasswordInProgress] = useState(false);
  const [requestNewPasswordDone, setRequestNewPasswordDone] = useState(false);
  const [requestNewPasswordInError, setRequestNewPasswordInError] = useState(false);
  const [isRequestNewPasswordDialogOpen, setIsRequestNewPasswordDialogOpen] = useState(false);

  const closeRequestNewPasswordDialog = useCallback(() => {
    setIsRequestNewPasswordDialogOpen(false);
  }, [setIsRequestNewPasswordDialogOpen]);

  const openRequestNewPasswordDialog = useCallback(() => {
    setIsRequestNewPasswordDialogOpen(true);
    setRequestNewPasswordInError(false);
    setRequestNewPasswordInProgress(false);
    setRequestNewPasswordDone(false);
  }, [setIsRequestNewPasswordDialogOpen]);

  const requestNewPasswordSubmit = async (data) => {
    closeRequestNewPasswordDialog();
    setRequestNewPasswordInProgress(true);
    const result = await handleRequestNewPassword(data);
    setRequestNewPasswordInProgress(false);
    if (result === true) setRequestNewPasswordDone(true);
    else setRequestNewPasswordInError(true);
  };

  return (
    <LoginContainer>
      <LoginCard elevation={3}>
        <Container>
          <DivLine>
            <img src={uphocLogo} height={40} alt="SecretariatUphocLogo" style={{ margin: '8px' }} />
            <img src={cerpanLogo} height={40} alt="SecretariatCerpanLogo" style={{ margin: '8px' }} />
            <img src={applLogo} height={40} alt="SecretariatApplLogo" style={{ margin: '8px' }} />
            <img src={apnlLogo} height={40} alt="SecretariatApnlLogo" style={{ margin: '8px' }} />
          </DivLine>
          <Form
            onSubmit={handleLogin}
            render={({ handleSubmit, hasValidationErrors, dirty }) => (
              <form onSubmit={handleSubmit}>
                <h1>
                  {transferModuleMode == 'standard' && <>Pharmagarde</>}
                  {transferModuleMode == 'dedicated' && <>Pharmamove</>}
                  {appStage != 'prod' && (
                    <span className={css({ color: 'red', fontSize: '30px', fontWeight: 'bold', paddingLeft: '10px' })}>
                      &nbsp;-&nbsp;TEST
                    </span>
                  )}
                  <br />
                </h1>
                <UserGuideLink UserGuideLabel={translate(TLCode.UserGuideManual)} />
                <br />
                <br />
                <TextInputFormField
                  name="username"
                  required={true}
                  inline={true}
                  fill={true}
                  validate={[]}
                  placeholder={TLCode.LOGIN_Login}
                />
                <PasswordInputFormField
                  name="password"
                  required={true}
                  inline={true}
                  fill={true}
                  placeholder={TLCode.LOGIN_Password}
                />
                <DivLine />
                {showWarningErrorLogin ? (
                  <LoginErrorContainer>{translate(TLCode.LOGIN_AuthenticationError)}</LoginErrorContainer>
                ) : (
                  <></>
                )}
                <DivLine>
                  <DivCenter>
                    {
                      <Button
                        type={'submit'}
                        text="Log in"
                        intent={Intent.PRIMARY}
                        disabled={hasValidationErrors || !dirty}
                      />
                    }
                  </DivCenter>
                </DivLine>
                <DivLine>
                  <DivCenter>
                    {
                      <a onClick={openRequestNewPasswordDialog}>
                        <TranslatedMessage id={TLCode.LOGIN_NewPassword} />
                      </a>
                    }
                  </DivCenter>
                </DivLine>
                <PasswordResetMailConfirmation
                  translate={translate}
                  inError={requestNewPasswordInError}
                  inProgress={requestNewPasswordInProgress}
                  done={requestNewPasswordDone}
                />
                <Dialog
                  isOpen={isRequestNewPasswordDialogOpen}
                  onClose={closeRequestNewPasswordDialog}
                  icon={IconNames.EDIT}
                  title={<TranslatedMessage id={TLCode.LOGIN_RequestNewPassword} />}
                  canOutsideClickClose={false}
                >
                  <Form
                    onSubmit={requestNewPasswordSubmit}
                    render={({ handleSubmit, hasValidationErrors }) => (
                      <form onSubmit={handleSubmit}>
                        <Container>
                          <br />
                          <TextInputFormField
                            name="username"
                            required={true}
                            inline={true}
                            fill={true}
                            validate={[]}
                            placeholder={TLCode.Username}
                          />
                          <TextInputFormField
                            name="email"
                            required={true}
                            inline={true}
                            fill={true}
                            validate={[]}
                            placeholder={TLCode.Email}
                          />
                          <DivLine>
                            <SpanWarning>
                              <TranslatedMessage id={TLCode.LOGIN_EmailAPB} />
                            </SpanWarning>
                          </DivLine>
                          <br />
                          <div className={Classes.DIALOG_FOOTER}>
                            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                              <Button
                                text={<TranslatedMessage id={TLCode.GENERAL_Cancel} />}
                                onClick={closeRequestNewPasswordDialog}
                              />
                              <Button
                                type="submit"
                                intent={Intent.PRIMARY}
                                disabled={hasValidationErrors}
                                text={<TranslatedMessage id={TLCode.GENERAL_Validate} />}
                              />
                            </div>
                          </div>
                        </Container>
                      </form>
                    )}
                  />
                </Dialog>
              </form>
            )}
          />
          <br />
          {languageSelectionComponent}
          <br />
          UI v{appVersion} WebApi v{webApiVersion}
        </Container>
      </LoginCard>
    </LoginContainer>
  );
};

@inject('AuthenticationStore', 'ApplicationStore', 'LocaleStore')
@observer
class LoginPage extends React.Component<LoginPageProps, LoginPageState> {
  constructor(props: LoginPageProps) {
    super(props);
    this.props.AuthenticationStore!.logout();
  }

  state = {
    transferModuleMode: 'unknown',
    appStage: 'prod',
    authenticationFailed: false,
  } as LoginPageState;

  languageSelectionComponent = () => {
    return (
      <Select
        defaultValue={this.props.LocaleStore!.currentLocale}
        style={{ width: 80 }}
        onChange={(e) => this.props.LocaleStore!.setCurrentLocale(e)}
      >
        {this.props.LocaleStore!.allLocales.map((locale) => (
          <Select.Option key={locale} value={locale}>
            {locale.toUpperCase()}
          </Select.Option>
        ))}
      </Select>
    );
  };

  async componentDidMount() {
    const transferModuleMode = await this.props.ApplicationStore!.getTransferModuleMode();
    const isProd = await this.props.ApplicationStore!.isProductionEnvironment();
    // @ts-ignore
    const appStage = isProd == true ? 'prod' : 'test';
    // @ts-ignore
    this.setState({ transferModuleMode, appStage });
  }

  render() {
    const props = this.props;
    const state = this.state;
    return (
      <Login
        handleLogin={async (data) => {
          const authenticationSuccessful = ((await this.props.AuthenticationStore!.authenticate(
            data.username,
            data.password,
          )) as unknown) as boolean;
          if (!authenticationSuccessful) {
            this.setState({ authenticationFailed: true });
          }
        }}
        handleRequestNewPassword={(data) => {
          return (this.props.AuthenticationStore!.requestNewPassword(data.username, data.email) as unknown) as boolean;
        }}
        appVersion={this.props.ApplicationStore!.appVersion}
        webApiVersion={this.props.ApplicationStore!.backendVersion}
        {...props}
        languageSelectionComponent={this.languageSelectionComponent()}
        transferModuleMode={state.transferModuleMode!}
        appStage={state.appStage!}
        showWarningErrorLogin={state.authenticationFailed}
      />
    );
  }
}

export default withTranslate(LoginPage);
